import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from './Layout'
import { useAuth } from '../context/AuthContext'
import { getUser } from '../services/profiles'
import { getReferralStatus } from '../services/bounty'
import { humanizeApiError } from '../services/client'

const POLL_MS = 30_000
const PAGE_SIZE = 20
const DEFAULT_REQUIRED = 10

const fmt = n => Number(n ?? 0).toLocaleString()

// Segments of the accumulation bar, in fill order.
const SEGMENTS = [
  { key: 'claimedReferrals', label: 'Paid', cls: 'bg-primary-container' },
  { key: 'availableReferrals', label: 'Being paid', cls: 'bg-primary-container/50' },
  { key: 'lockedReferrals', label: 'On hold', cls: 'bg-error/60' },
  { key: 'pendingReferrals', label: 'Pending', cls: 'bg-on-background/25' },
]

function AccumulationBar({ summary }) {
  const total = summary.totalReferrals || 0
  if (total === 0) return null
  const aria = SEGMENTS.map(s => `${fmt(summary[s.key])} ${s.label.toLowerCase()}`).join(', ')
  return (
    <div className="flex flex-col gap-2">
      <div
        role="img"
        aria-label={`${fmt(total)} friends referred: ${aria}`}
        className="flex h-3 w-full overflow-hidden border-2 border-on-background/30 bg-surface"
      >
        {SEGMENTS.map(s => {
          const pct = ((summary[s.key] || 0) / total) * 100
          return (
            <div
              key={s.key}
              className={`${s.cls} h-full transition-[width] duration-700 ease-out motion-reduce:transition-none`}
              style={{ width: `${pct}%` }}
            />
          )
        })}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1">
        {SEGMENTS.map(s => (
          <li key={s.key} className="flex items-center gap-1.5 text-[10px] font-bold text-on-surface-variant">
            <span className={`inline-block w-2.5 h-2.5 border border-on-background/30 ${s.cls}`} />
            {s.label} {fmt(summary[s.key])}
          </li>
        ))}
      </ul>
    </div>
  )
}

function Stat({ label, value, accent }) {
  return (
    <div className="border-2 border-on-background/20 bg-surface p-3 min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">{label}</p>
      <p className={`mt-1 font-extrabold text-[20px] leading-none ${accent ? 'text-primary-container' : ''}`}>
        {value}
      </p>
    </div>
  )
}

// Why money is or isn't flowing. Only shown when friends are waiting.
function Notice({ summary, email }) {
  const waiting = (summary.availableReferrals || 0) + (summary.lockedReferrals || 0)
  if (waiting <= 0) return null

  const lines = []
  if (summary.availableCpt > 0) lines.push(`${fmt(summary.availableCpt)} CPT is on its way.`)

  let reason = null
  let action = null
  switch (summary.payoutBlockedReason) {
    case 'email_unverified':
      reason = "Verify your email to receive your referral rewards. They'll be paid as soon as you do."
      action = (
        <Link to="/check-email" state={{ email }} className="font-bold underline">
          Verify your email
        </Link>
      )
      break
    case 'banned':
      reason = 'Referral payouts are paused on your account.'
      break
    case 'campaign_closed':
      reason = "This campaign isn't paying out right now."
      break
    case 'kyc_required':
      reason = 'This campaign requires identity verification before payouts.'
      break
    default:
      if (summary.limitedBy === 'user_cap') reason = "You've reached the reward limit for this campaign."
      else if (summary.limitedBy === 'no_price') reason = 'Payouts are temporarily unavailable. Please check back soon.'
  }
  if (reason) lines.push(reason)
  if (lines.length === 0) return null

  return (
    <div role="status" className="border-2 border-on-background/30 bg-surface-container-low p-3 text-[11px] leading-snug flex flex-col gap-1">
      {lines.map(l => <p key={l}>{l}</p>)}
      {action}
    </div>
  )
}

function FriendRow({ item }) {
  const name = item.fullName || item.username || 'New member'
  const p = item.progress
  const pct = Math.round(Math.min(1, Math.max(0, item.completion ?? 0)) * 100)
  return (
    <li className="flex items-center gap-3 border-t border-on-background/10 py-3 first:border-t-0">
      {item.avatarUrl ? (
        <img src={item.avatarUrl} alt="" className="w-8 h-8 object-cover border-2 border-on-background/20 shrink-0" />
      ) : (
        <span className="w-8 h-8 flex items-center justify-center border-2 border-on-background/20 shrink-0">
          <Icon name="person" className="text-[16px]" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-bold text-[12px] truncate">{name}</p>
        {p ? (
          <>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              aria-label={`${name}: ${pct}% of requirements met`}
              className="mt-1 h-1.5 w-full border border-on-background/30 bg-surface"
            >
              <div
                className="h-full bg-primary-container transition-[width] duration-700 ease-out motion-reduce:transition-none"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-1 text-[10px] font-bold text-on-surface-variant">
              {Math.min(p.posts, p.requiredPosts)}/{p.requiredPosts} posts ·{' '}
              {Math.min(p.comments, p.requiredComments)}/{p.requiredComments} comments
              {p.emailVerified === false && ' · email not verified'}
            </p>
          </>
        ) : (
          <p className="mt-0.5 text-[10px] font-bold text-primary-container">{fmt(item.cptAmount)} CPT paid</p>
        )}
      </div>
    </li>
  )
}

export function ReferralDashboard({ campaign }) {
  const { user } = useAuth()
  const [tab, setTab] = useState('pending')
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [referralLink, setReferralLink] = useState('')
  const [copied, setCopied] = useState(false)
  const requiredRef = useRef({ posts: DEFAULT_REQUIRED, comments: DEFAULT_REQUIRED })

  // Fetch the current tab's page. Keeps the previous data on screen while
  // refreshing, and on a failed poll, so numbers never blank out.
  const load = useCallback(() => {
    return getReferralStatus(campaign.id, { state: tab, page, limit: PAGE_SIZE })
      .then(res => { setData(res); setError(null) })
      .catch(err => setError(humanizeApiError(err)))
  }, [campaign.id, tab, page])

  useEffect(() => {
    load()
    const id = setInterval(() => { if (!document.hidden) load() }, POLL_MS)
    const onVisible = () => { if (!document.hidden) load() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible) }
  }, [load])

  // Referral link for the empty state (same derivation as the Profile page).
  useEffect(() => {
    let cancelled = false
    getUser()
      .then(res => {
        const p = res?.data ?? res
        const code = p?.referralCode ?? (p?.referralLink ? p.referralLink.split('/').pop() : null)
        if (!cancelled && code) setReferralLink(`${window.location.origin}/login/${code}`)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const summary = data?.summary
  const items = data?.items ?? []

  // If the active tab's bucket just emptied (e.g. everyone got paid), fall back.
  useEffect(() => {
    if (!summary) return
    if ((tab === 'available' && summary.availableReferrals === 0) ||
        (tab === 'locked' && summary.lockedReferrals === 0)) {
      setTab('pending'); setPage(1)
    }
  }, [summary, tab])

  // Requirements aren't in `summary`; read them off any item, else 10/10.
  const withProgress = items.find(i => i.progress)
  if (withProgress) {
    requiredRef.current = {
      posts: withProgress.progress.requiredPosts,
      comments: withProgress.progress.requiredComments,
    }
  }
  const req = requiredRef.current

  const copyLink = () => {
    navigator.clipboard?.writeText(referralLink).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {})
  }

  const rule = (
    <p className="text-[11px] leading-snug text-on-surface-variant">
      Every friend who verifies their email, posts {req.posts} times and comments {req.comments} times earns you{' '}
      {fmt(summary?.rewardPerReferralCpt ?? campaign.rewardAmountCpt)} CPT, paid automatically.
    </p>
  )

  if (!data && !error) {
    return <div className="sm:col-span-2 h-40 bg-surface-container animate-pulse" aria-busy="true" />
  }

  if (!summary) {
    return (
      <section className="border-2 border-on-background/20 bg-surface-container p-4 flex flex-col gap-2">
        <h3 className="font-extrabold text-[13px]">{campaign.title}</h3>
        <p role="alert" className="text-[11px] font-bold text-error">{error}</p>
        <button onClick={load} className="self-start px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-on-background hover:text-surface">
          Try again
        </button>
      </section>
    )
  }

  // Tabs: Pending / Paid always; Being paid / On hold only when non-zero.
  const tabs = [
    { key: 'pending', label: 'Pending', count: summary.pendingReferrals },
    { key: 'claimed', label: 'Paid', count: summary.claimedReferrals },
    ...(summary.availableReferrals > 0 ? [{ key: 'available', label: 'Being paid', count: summary.availableReferrals }] : []),
    ...(summary.lockedReferrals > 0 ? [{ key: 'locked', label: 'On hold', count: summary.lockedReferrals }] : []),
  ]
  const totalPages = Math.max(1, Math.ceil((data.total ?? 0) / PAGE_SIZE))
  const emptyTab = { pending: 'No pending friends.', claimed: 'No paid friends yet.', available: 'Nobody is being paid right now.', locked: 'Nobody is on hold.' }[tab]

  return (
    <section aria-label={campaign.title} className="border-2 border-on-background/20 bg-surface-container p-3 sm:p-4 flex flex-col gap-4 min-w-0">
      <div>
        <div className="flex items-center gap-2">
          <Icon name="person_add" className="text-[16px]" />
          <h3 className="font-extrabold text-[13px] leading-tight break-words">{campaign.title}</h3>
        </div>
        <div className="mt-1">{rule}</div>
      </div>

      {error && <p role="alert" className="text-[10px] font-bold text-error">Couldn't refresh: {error}</p>}

      {summary.totalReferrals === 0 ? (
        <div className="border-2 border-dashed border-on-background/20 p-4 flex flex-col gap-2">
          <p className="font-bold text-[12px]">No referrals yet</p>
          <p className="text-[11px] text-on-surface-variant">Share your link. Each friend who joins and completes the steps above earns you CPT.</p>
          {referralLink && (
            <div className="flex items-center gap-2 flex-wrap">
              <code className="flex-1 min-w-0 truncate bg-surface border-2 border-on-background/20 px-2 py-1.5 text-[11px] font-mono">{referralLink}</code>
              <button onClick={copyLink} className="px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-on-background hover:text-surface">
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Total referrals" value={fmt(summary.totalReferrals)} />
            <Stat label="Qualified" value={fmt(summary.qualifiedReferrals)} />
            <Stat label="Pending" value={fmt(summary.pendingReferrals)} />
            <Stat label="CPT earned" value={fmt(summary.claimedCpt)} accent />
          </div>

          <AccumulationBar summary={summary} />
          <Notice summary={summary} email={user?.email} />

          <div>
            <div role="tablist" aria-label="Friends by status" className="flex gap-1 flex-wrap border-b-2 border-on-background/20">
              {tabs.map(t => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => { setTab(t.key); setPage(1) }}
                  className={`px-3 py-2 -mb-0.5 text-[10px] font-bold uppercase tracking-widest border-b-2 focus:outline-none focus-visible:bg-surface-container-high ${
                    tab === t.key ? 'border-primary-container text-on-background' : 'border-transparent text-on-surface-variant hover:text-on-background'
                  }`}
                >
                  {t.label} ({fmt(t.count)})
                </button>
              ))}
            </div>

            {items.length === 0 ? (
              <p className="py-4 text-[11px] text-on-surface-variant">{emptyTab}</p>
            ) : (
              <ul>{items.map(i => <FriendRow key={i.profileId} item={i} />)}</ul>
            )}

            {totalPages > 1 && (
              <nav aria-label="Pagination" className="flex items-center justify-between gap-2 pt-2 border-t border-on-background/10">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest disabled:opacity-40">Previous</button>
                <span className="text-[10px] font-bold text-on-surface-variant">Page {page} of {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest disabled:opacity-40">Next</button>
              </nav>
            )}
          </div>
        </>
      )}
    </section>
  )
}
